import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Mail, Send, Star, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const monthNow = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const scoreColour = (s) =>
  s >= 4 ? "bg-emerald-100 text-emerald-800" : s === 3 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800";

export const CustomerPulsePanel = ({ canSend }) => {
  const [month, setMonth] = useState(monthNow());
  const [overview, setOverview] = useState(null);
  const [cands, setCands] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [settings, setSettings] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [job, setJob] = useState(null);

  const pollJob = async (jobId) => {
    try {
      const { data } = await api.get("/csat/send/status", { params: jobId ? { job_id: jobId } : {} });
      if (!data.job) return;
      setJob(data.job);
      if (data.job.status === "running") {
        setTimeout(() => pollJob(data.job.job_id), 2000);
      } else {
        setSending(false);
        toast.success(`Survey round finished — ${data.job.sent} sent${data.job.failed ? `, ${data.job.failed} failed` : ""}`);
        load();
      }
    } catch (e) { setSending(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/csat/send/status");
        if (data.job?.status === "running") { setSending(true); setJob(data.job); pollJob(data.job.job_id); }
      } catch (e) { /* no-op */ }
    })();
    // eslint-disable-next-line
  }, []);

  const toggleAutoSend = async () => {
    try {
      const { data } = await api.put("/csat/settings",
        { auto_send: !settings?.auto_send, throttle_days: settings?.throttle_days || 30 });
      setSettings(data);
      toast.success(data.auto_send
        ? "Auto-send ON — surveys go out automatically after each Completed Tasks import"
        : "Auto-send OFF — use the Send button");
    } catch (e) { toast.error("Failed to update setting"); }
  };

  const load = async () => {
    setBusy(true);
    try {
      const [{ data: ov }, { data: cd }, { data: st }, { data: rv }] = await Promise.all([
        api.get("/csat/overview"),
        api.get("/csat/candidates", { params: { month } }),
        api.get("/csat/settings"),
        api.get("/csat/account-reviews"),
      ]);
      setOverview(ov);
      setCands(cd);
      setSettings(st);
      setReviews(rv.reviews || []);
    } catch (e) {
      toast.error("Failed to load Customer Pulse");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const excludeClient = async (name) => {
    const reason = window.prompt(`Never survey "${name}"?\n(e.g. contract client — surveyed via account reviews instead)\n\nReason (optional):`);
    if (reason === null) return;
    try {
      await api.post("/csat/exclusions", { client_name: name, exclude: true, reason });
      toast.success(`${name} excluded from CSAT surveys`);
      await load();
    } catch (e) { toast.error("Failed to exclude"); }
  };

  const sendAll = async () => {
    const n = cands?.candidates?.length || 0;
    if (!n) return;
    if (!window.confirm(`Email a 1-click CSAT survey to ${n} client contact${n === 1 ? "" : "s"} about their ${month} jobs? Sending runs in the background (~1-2/sec) — you can watch progress here. Each client is only surveyed once every ${cands.throttle_days} days.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/csat/send", { month });
      if (data.queued) {
        setJob({ job_id: data.job_id, month, total: data.total, sent: 0, failed: 0, status: "running", failures: [] });
        toast.success(`Sending ${data.total} surveys in the background…`);
        pollJob(data.job_id);
      } else {
        setSending(false);
        toast.info(data.detail || "Nothing to send");
      }
    } catch (e) {
      setSending(false);
      toast.error(e.response?.data?.detail || "Send failed");
    }
  };

  const escalate = async (r) => {
    if (!window.confirm(`Email this ${r.score}/5 rating from ${r.client_name} to the ${r.bu_code} BU manager (fallback: executives) with the contact, jobs and comment?`)) return;
    try {
      const { data } = await api.post(`/csat/responses/${r.token}/escalate`);
      toast.success(`Escalation emailed to: ${(data.sent_to || []).join(", ")}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Escalation failed");
    }
  };

  const clearSuspect = async () => {
    if (!window.confirm("Clear suspect responses? This resets ratings that were recorded within 10 minutes of the send AND have no comment — the signature of email security scanners auto-clicking survey links. Clients' links stay valid so they can still respond genuinely. Responses with comments are kept.")) return;
    try {
      const { data } = await api.post("/csat/responses/clear-suspect");
      toast.success(`Cleared ${data.cleared} suspect response${data.cleared === 1 ? "" : "s"}`);
      await load();
    } catch (e) { toast.error("Failed to clear suspect responses"); }
  };

  const resetMonth = async (m) => {
    if (!window.confirm(`Clear ${m}'s UNANSWERED sent-records? Clients that never received their email (failed send) can then be re-surveyed. Recorded responses are kept.`)) return;
    try {
      const { data } = await api.delete("/csat/tokens", { params: { month: m } });
      toast.success(`Cleared ${data.deleted} unanswered record${data.deleted === 1 ? "" : "s"} for ${m}`);
      await load();
    } catch (e) { toast.error("Reset failed"); }
  };

  const months = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(); d.setMonth(d.getMonth() - i);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }

  return (
    <div className="space-y-4" data-testid="customer-pulse-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1.5"><Star className="w-3.5 h-3.5 text-amber-500" /> Avg CSAT</div>
          <div className="mt-1 font-display text-2xl" data-testid="csat-avg">{overview?.avg_score ?? "—"}<span className="text-sm text-slate-400"> / 5</span></div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">Responses</div>
          <div className="mt-1 font-display text-2xl">{overview?.responded ?? "—"}<span className="text-sm text-slate-400"> of {overview?.sent ?? 0} sent</span></div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">Response rate</div>
          <div className="mt-1 font-display text-2xl">{overview?.response_rate_pct ?? 0}%</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">By BU</div>
          <div className="mt-1 text-xs text-slate-700 space-y-0.5 max-h-16 overflow-y-auto">
            {(overview?.per_bu || []).length === 0 && <span className="text-slate-400">No responses yet</span>}
            {(overview?.per_bu || []).map((b) => (
              <div key={b.bu_code} className="flex justify-between"><span className="font-mono">{b.bu_code}</span><span>{b.avg} (n={b.n})</span></div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-2 border-b border-slate-100">
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <Mail className="w-4 h-4" /> Survey candidates
            <span className="text-xs font-normal text-slate-400">clients with completed jobs, 1-click 1–5 rating email</span>
          </div>
          <div className="flex items-center gap-2">
            {canSend && settings && (
              <button onClick={toggleAutoSend} data-testid="pulse-auto-toggle"
                      className={`h-8 px-2.5 rounded-md text-xs font-medium border transition-colors ${
                        settings.auto_send
                          ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                          : "bg-white border-slate-200 text-slate-500"}`}>
                Auto-send {settings.auto_send ? "ON" : "OFF"}
              </button>
            )}
            <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="pulse-month"
                    className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white">
              {months.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="pulse-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
            </Button>
            {canSend && (
              <Button size="sm" onClick={sendAll} disabled={sending || !(cands?.candidates?.length)}
                      className="bg-gbe hover:bg-gbe/90" data-testid="pulse-send-all">
                {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
                Send {cands?.candidates?.length || 0} surveys
              </Button>
            )}
          </div>
        </div>
        {job && (
          <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50/60" data-testid="pulse-send-progress">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="font-medium text-slate-700">
                {job.status === "running"
                  ? <><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Sending {job.month} surveys… {job.sent + job.failed} / {job.total}</>
                  : <>Last round ({job.month}): {job.sent} / {job.total} sent</>}
              </span>
              <span className="text-slate-500 tabular-nums">
                {job.sent} sent{job.failed ? <span className="text-rose-600"> · {job.failed} failed</span> : ""}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full bg-gbe rounded-full transition-all duration-500"
                   style={{ width: `${job.total ? Math.round(((job.sent + job.failed) / job.total) * 100) : 0}%` }} />
            </div>
            {(job.failures || []).length > 0 && (
              <details className="mt-1.5 text-[11px] text-rose-700">
                <summary className="cursor-pointer">Failures ({job.failed})</summary>
                <div className="mt-1 space-y-0.5 max-h-24 overflow-y-auto">
                  {job.failures.map((f, i) => (
                    <div key={i}><span className="font-medium">{f.client_name}</span> ({f.email}) — {f.error}</div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}
        {busy && !cands && <div className="p-8 text-center text-slate-400 text-sm"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Loading…</div>}
        {cands && (
          <>
            <div className="px-4 py-1.5 text-[11px] text-slate-400 border-b border-slate-100" data-testid="pulse-skip-summary">
              {cands.skipped_no_email} skipped (no contact email) · {cands.skipped_recently_surveyed} skipped (surveyed within {cands.throttle_days} days) · {cands.skipped_excluded || 0} excluded (never survey)
              {cands.candidate_source === "aroflo" && (
                <span className="text-sky-600"> · candidates built from the live AroFlo sync (no Completed Tasks import for this month)</span>
              )}
              {(cands.skipped_generic_mailbox || 0) > 0 && (
                <span className="text-amber-600" title={(cands.generic_skipped || []).map((g) => `${g.client_name}: ${g.email}`).join("\n")}>
                  {" "}· {cands.skipped_generic_mailbox} skipped (generic mailbox — ap@/accounts@/invoices@…)
                </span>
              )}
            </div>
            <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
              {cands.candidates.length === 0 && (
                <div className="p-6 text-center text-sm text-slate-400">
                  {cands.source_rows === 0
                    ? <>No completed jobs found for {month} — neither a Completed Tasks import nor completed AroFlo jobs. Import the month's export (Import Hub) or run an AroFlo sync.</>
                    : <>No candidates for {month}.</>}
                </div>
              )}
              {cands.candidates.map((c, i) => (
                <div key={i} className="px-4 py-2 text-sm flex items-center gap-3" data-testid={`pulse-candidate-${i}`}>
                  <span className="flex-1 min-w-0 truncate text-slate-900">{c.client_name}</span>
                  <span className="font-mono text-xs text-slate-500">{c.bu_code}</span>
                  <span className="text-xs text-slate-400 truncate max-w-44" title={c.contact_name}>{c.email}</span>
                  <span className={`text-[9px] px-1.5 py-px rounded uppercase font-semibold ${
                    c.email_source === "job_contact" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                    title={c.email_source === "job_contact" ? "Contact who logged the jobs (from the export)" : "Client record / MYOB card"}>
                    {c.email_source === "job_contact" ? "job contact" : "client record"}
                  </span>
                  <Badge className="bg-slate-100 text-slate-600">{c.jobs} job{c.jobs === 1 ? "" : "s"}</Badge>
                  {canSend && (
                    <button onClick={() => excludeClient(c.client_name)} data-testid={`pulse-exclude-${i}`}
                            className="text-[10px] text-slate-400 hover:text-rose-600 whitespace-nowrap"
                            title="Never survey this client (e.g. contract client)">
                      never survey
                    </button>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white" data-testid="pulse-history">
        <div className="px-4 py-3 text-sm font-semibold text-slate-800 border-b border-slate-100">
          Survey history by month
          <span className="ml-2 text-[11px] font-normal text-slate-400">all survey rounds — results attribute to the month the jobs were completed</span>
        </div>
        {(overview?.per_month || []).length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400">No surveys sent yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100 bg-slate-50/60">
                <th className="text-left font-medium px-4 py-2">Month</th>
                <th className="text-right font-medium px-4 py-2">Sent</th>
                <th className="text-right font-medium px-4 py-2">Responded</th>
                <th className="text-right font-medium px-4 py-2">Rate</th>
                <th className="text-right font-medium px-4 py-2">Avg score</th>
                {canSend && <th className="px-4 py-2" />}
              </tr>
            </thead>
            <tbody>
              {(overview?.per_month || []).map((m) => (
                <tr key={m.month} className="border-b border-slate-50">
                  <td className="px-4 py-1.5 font-mono text-xs text-slate-700">{m.month}</td>
                  <td className="px-4 py-1.5 text-right tabular-nums">{m.sent}</td>
                  <td className="px-4 py-1.5 text-right tabular-nums">{m.responded}</td>
                  <td className="px-4 py-1.5 text-right tabular-nums text-slate-500">{m.response_rate_pct}%</td>
                  <td className="px-4 py-1.5 text-right tabular-nums font-medium">{m.avg ?? "—"}</td>
                  {canSend && (
                    <td className="px-4 py-1.5 text-right">
                      {m.sent > m.responded && (
                        <button onClick={() => resetMonth(m.month)} data-testid={`pulse-reset-${m.month}`}
                                className="text-[10px] text-slate-400 hover:text-rose-600 whitespace-nowrap"
                                title="Clear this month's unanswered sent-records so failed sends can be retried (responses are kept)">
                          reset unanswered
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        <div className="px-4 py-3 flex items-center justify-between border-b border-slate-100">
          <span className="text-sm font-semibold text-slate-800">Recent responses</span>
          {canSend && (overview?.recent || []).some((r) => r.instant) && (
            <button onClick={clearSuspect} data-testid="pulse-clear-suspect"
                    className="text-[11px] px-2 py-1 rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                    title="Ratings recorded within minutes of the send with no comment are usually email security scanners auto-clicking links — clear them so clients can respond genuinely">
              Clear bot responses
            </button>
          )}
        </div>
        <div className="divide-y divide-slate-100 max-h-72 overflow-y-auto">
          {(overview?.recent || []).length === 0 && <div className="p-6 text-center text-sm text-slate-400">No responses yet — send your first surveys above.</div>}
          {(overview?.recent || []).map((r, i) => (
            <div key={i} className="px-4 py-2.5 text-sm flex items-start gap-3" data-testid={`pulse-response-${i}`}>
              <Badge className={scoreColour(r.score)}>{r.score}/5</Badge>
              <span className="flex-1 min-w-0">
                <span className="text-slate-900">{r.client_name}</span>
                <span className="text-xs text-slate-400"> · {r.bu_code} · {r.period_month}</span>
                {r.instant && (
                  <span className="ml-1.5 text-[9px] px-1.5 py-px rounded uppercase font-semibold bg-amber-100 text-amber-800"
                        title="Recorded within minutes of the send — likely an email security scanner, not the client">
                    instant — likely scanner
                  </span>
                )}
                {(r.contact_name || r.to_email) && (
                  <span className="block text-xs text-slate-500 mt-0.5">
                    {r.contact_name || "Contact"}{r.to_email ? ` · ${r.to_email}` : ""}
                  </span>
                )}
                {(r.refs || []).length > 0 && (
                  <span className="block text-[11px] text-slate-400 mt-0.5 font-mono truncate"
                        title={(r.refs || []).join(", ")}>
                    Jobs ({r.jobs}): {(r.refs || []).slice(0, 4).join(",  ")}{r.jobs > 4 ? ` +${r.jobs - 4} more` : ""}
                  </span>
                )}
                {r.comment && <span className="block text-xs text-slate-500 mt-0.5">"{r.comment}"</span>}
              </span>
              {canSend && r.score <= 2 && (
                <button onClick={() => escalate(r)} data-testid={`pulse-escalate-${i}`}
                        className="text-[10px] px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50 whitespace-nowrap"
                        title="Email this low rating to the BU manager (with contact, jobs and comment)">
                  Send to BU manager
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white" data-testid="account-reviews">
        <div className="px-4 py-3 text-sm font-semibold text-slate-800 border-b border-slate-100">
          Contract client account reviews
          <span className="ml-2 text-[11px] font-normal text-slate-400">internal health check for "never survey" clients, from hub data</span>
        </div>
        <div className="divide-y divide-slate-100 max-h-80 overflow-y-auto">
          {(reviews || []).length === 0 && (
            <div className="p-6 text-center text-sm text-slate-400">
              No contract clients yet — click "never survey" on a candidate above and their account review appears here.
            </div>
          )}
          {(reviews || []).map((r, i) => (
            <div key={i} className="px-4 py-2.5 text-sm flex items-center gap-3 flex-wrap" data-testid={`account-review-${i}`}>
              <Badge className={
                r.rating === "strong" ? "bg-emerald-100 text-emerald-800"
                : r.rating === "stable" ? "bg-sky-100 text-sky-800"
                : r.rating === "watch" ? "bg-amber-100 text-amber-800"
                : "bg-rose-100 text-rose-800"}>
                {r.health} · {r.rating.replace("_", " ")}
              </Badge>
              <span className="flex-1 min-w-32 truncate text-slate-900" title={r.reason}>{r.client_name}</span>
              <span className="text-[11px] text-slate-500 tabular-nums">{r.jobs_90d} jobs 90d · {r.jobs_12m} 12m</span>
              <span className="text-[11px] tabular-nums">
                <span className="text-emerald-700">{r.compliments_12m}👍</span>{" "}
                <span className={r.open_complaints ? "text-rose-700 font-semibold" : "text-slate-500"}>
                  {r.complaints_12m} complaints{r.open_complaints ? ` (${r.open_complaints} open)` : ""}
                </span>
              </span>
              {r.overdue > 0 && (
                <span className={`text-[11px] tabular-nums ${r.overdue_180 > 0 ? "text-rose-700 font-semibold" : "text-slate-500"}`}>
                  ${Math.round(r.overdue).toLocaleString()} overdue{r.overdue_180 > 0 ? " (180+)" : ""}
                </span>
              )}
              {r.csat_avg != null && <span className="text-[11px] text-slate-500">CSAT {r.csat_avg}</span>}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-sky-200 bg-sky-50 p-4 text-xs text-sky-900">
        <span className="font-semibold">Capturing phone/email feedback automatically:</span> forward any client email to your
        feedback mailbox and Power Automate posts it to <span className="font-mono">/api/feedback/email-intake</span> —
        AI classifies it as a compliment, complaint or query, matches the client and BU, and it lands in this register
        (feeding the Customer Service KPIs). Phone feedback: use the "Log feedback" button — 15 seconds.
      </div>
    </div>
  );
};
